import React, { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { StockMenu, WatchAdd } from "@/components/WatchAdd";
import { copySymbols } from "@/pages/Scanner";
import { Clock, Copy } from "lucide-react";
import { useFlow, FlowTags, FlowNote } from "@/components/FlowMarks";

const TABS = [
  ["d_up", "Above TFR D", "text-emerald-600"], ["d_down", "Below TFR D", "text-rose-600"],
];
const ZONE = {
  D_narrow: ["NZ D", "Narrow Day Flow Zone in force today"], D_inside: ["NZ IN D", "Narrow Inside Day Flow Zone in force today"],
  W_narrow: ["NZ W", "Narrow Week Flow Zone in force this week"], W_inside: ["NZ IN W", "Narrow Inside Week Flow Zone in force this week"],
};
const chip = (on) => `h-5 px-1.5 text-[9px] font-mono border transition-colors ${on ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-500 hover:border-blue-500"}`;

const Filters = ({ sectors, sec, setSec, marked, setMarked }) => (
  <div className="mb-2 space-y-1">
    <button onClick={() => setMarked(!marked)} data-testid="poc-marked-toggle" aria-pressed={marked}
      className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
      <span className={`relative h-3.5 w-6 rounded-full transition-colors ${marked ? "bg-amber-400" : "bg-slate-200"}`}>
        <span className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-black transition-transform ${marked ? "translate-x-3" : "translate-x-0.5"}`} />
      </span>
      <span className={marked ? "text-amber-300 font-bold" : ""}>Only BIG / OI / NZ marked</span>
    </button>
    {sectors.length > 0 && (
      <div className="flex flex-wrap gap-1" data-testid="poc-sector-chips">
        <button onClick={() => setSec(null)} className={chip(!sec)} data-testid="poc-sector-all">All</button>
        {sectors.map(([s, n]) => (
          <button key={s} onClick={() => setSec(sec === s ? null : s)} className={chip(sec === s)}
            data-testid={`poc-sector-${s.replace(/\W+/g, "-")}`}>{s} <span className="opacity-60">{n}</span></button>
        ))}
      </div>
    )}
  </div>
);

const Row = ({ r, tab, hot, m }) => (
  <div className="flex items-center gap-1">
    <StockMenu instrumentId={r.instrument_id} symbol={r.symbol}>
      <button className={`flex-1 min-w-0 flex items-center justify-between gap-2 text-xs font-mono px-1.5 py-1 hover:bg-blue-500/10 transition-colors text-left ${r.big ? "border-l-2 border-amber-400 bg-amber-500/5" : r.oi ? "border-l-2 border-cyan-400 bg-cyan-500/5" : r.zone?.length ? "border-l-2 border-violet-400 bg-violet-500/5" : ""}`} data-testid={`poc-row-${r.symbol}`}>
        <span className="w-52 shrink-0 leading-tight text-slate-900 font-semibold">{r.symbol}<FlowTags m={m} testid={`poc-${r.symbol}`} />{hot?.top?.includes(r.sector) && <span className="ml-1 text-[8px] px-1 font-bold bg-emerald-600 text-white" title="Top-2 performing sector today" data-testid={`poc-top-${r.symbol}`}>TOP</span>}{hot?.bottom?.includes(r.sector) && <span className="ml-1 text-[8px] px-1 font-bold bg-rose-600 text-white" title="Bottom-2 performing sector today" data-testid={`poc-worst-${r.symbol}`}>WORST</span>}{r.big > 0 && <span className={`ml-1 text-[9px] px-1 font-bold ${r.big === 3 ? "bg-amber-400 text-black" : "bg-amber-500/20 text-amber-300 border border-amber-500/50"}`} title={`Crossing candle volume ${r.vol_x}× usual for that time`} data-testid={`poc-big-${r.symbol}`}>BIG {r.big}×</span>}{r.oi && <span className="ml-1 text-[9px] px-1 font-bold bg-cyan-500/15 text-cyan-300 border border-cyan-500/50" title={`#${r.oi.rank} OI gainer · OI +${r.oi.oi_chg}% · ${r.oi.buildup}`} data-testid={`poc-oi-${r.symbol}`}>OI #{r.oi.rank}</span>}{r.open_rv && <span className="ml-1 text-[9px] px-1 font-bold bg-orange-500 text-black" title={`Opening 09:15 candle crossed the line with ${r.open_rv}× the volume of the previous 21 candles`} data-testid={`poc-open-${r.symbol}`}>OPEN {r.open_rv}×</span>}{(r.zone || []).map((z) => <span key={z} className="ml-1 text-[9px] px-1 font-bold bg-violet-500/15 text-violet-300 border border-violet-500/50" title={ZONE[z][1]} data-testid={`poc-zone-${z}-${r.symbol}`}>{ZONE[z][0]}</span>)}<span className="block text-[9px] text-slate-500 font-normal">{r.sector}{r.first_at && <span className="text-sky-400/80" data-testid={`poc-first-${r.symbol}`}> · 1st {r.first_at}{r.crosses > 1 ? ` · ${r.crosses}×` : ""}</span>}</span></span>
        <span className="text-slate-600">{inr(r.ltp)}</span>
        <span className="text-slate-500 text-[10px] whitespace-nowrap">{tab[0] === "d" ? "D" : "W"} {inr(r.poc)}</span>
        <span className={r.dist_pct >= 0 ? "text-emerald-600" : "text-rose-600"}>{r.dist_pct > 0 ? "+" : ""}{r.dist_pct}%</span>
        <span className="text-[10px] px-1.5 py-px border border-blue-500/40 bg-blue-500/10 text-sky-300 flex items-center gap-1 whitespace-nowrap" title={`First cross ${r.first_at} (${r.first_dir}) · latest ${r.cross_at} · ${r.crosses} cross(es) today`} data-testid={`poc-time-${r.symbol}`}><Clock className="h-2.5 w-2.5" />{r.cross_at}</span>
      </button>
    </StockMenu>
    <WatchAdd instrumentId={r.instrument_id} symbol={r.symbol} />
  </div>
);

export const PocCrossPanel = ({ hot }) => {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("d_up");
  const [sec, setSec] = useState(null);
  const [marked, setMarked] = useState(false);
  const [tf, setTf] = useState("5m");
  useEffect(() => {
    const load = () => api.get("/today/poc-cross").then((r) => setD(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  const flow = useFlow();
  const keep = (r) => flow.pass(r.symbol) && (!marked || r.big > 0 || r.oi || r.zone?.length || r.open_rv) && (!sec || r.sector === sec);
  const sectors = useMemo(() => {
    const c = {};
    Object.values(d?.tf?.[tf] || d?.lists || {}).forEach((rows) => rows.forEach((r) => { if (r.sector) (c[r.sector] ||= new Set()).add(r.symbol); }));
    return Object.entries(c).map(([s, set]) => [s, set.size]).sort((a, b) => b[1] - a[1]);
  }, [d, tf]);
  if (!d) return <p className="text-xs font-mono text-slate-500">Loading…</p>;
  const L = d.tf?.[tf] || d.lists || {};
  const rows = (L[tab] || []).filter(keep);
  return (
    <div data-testid="poc-panel">
      <div className="flex items-center gap-1 mb-2">
        <span className="text-[9px] font-mono text-slate-500 mr-1">CANDLE</span>
        {["5m", "15m"].map((t) => (
          <button key={t} onClick={() => setTf(t)} className={chip(tf === t)} data-testid={`poc-tf-${t}`}>{t}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-1 mb-2">
        {TABS.map(([k, label, c]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`poc-tab-${k}`}
            className={`text-[10px] font-mono font-bold px-1 py-1 border ${tab === k ? `border-blue-500 ${c} bg-blue-500/15` : "border-slate-200 text-slate-500"}`}>
            {label} <span className="text-slate-500" data-testid={`poc-count-${k}`}>{(L[k] || []).filter(keep).length}</span>
          </button>
        ))}
      </div>
      <Filters sectors={sectors} sec={sec} setSec={setSec} marked={marked} setMarked={setMarked} />
      <div className="flex justify-between items-center mb-1">
        <span className="text-[9px] font-mono text-slate-500">{tf} close crossed the line today · 1st = first cross, clock = latest · BIG = crossing candle ≥2× usual volume · OI # = top 20 OI gainer · NZ = narrow Flow Zone (IN = inside) · OPEN = 09:15 candle crossed with &gt;3× volume of prior 21 candles · marked first</span>
        {rows.length > 0 && <button onClick={() => copySymbols(rows.map((r) => r.symbol), "symbols")} data-testid="poc-copy"
          className="text-[10px] font-mono text-blue-600 flex items-center gap-1"><Copy className="h-3 w-3" />copy</button>}
      </div>
      <FlowNote />
      {!rows.length && <p className="text-xs font-mono text-slate-500 py-3" data-testid="poc-empty">{marked || sec ? "No crosses match these filters" : "No crosses yet today"}</p>}
      <div className="space-y-px max-h-80 overflow-y-auto">
        {rows.map((r) => <Row key={r.symbol} r={r} tab={tab} hot={hot} m={flow.marks[r.symbol]} />)}
      </div>
    </div>
  );
};
